import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { Chip, Snackbar, Alert } from '@mui/material';
import { DownloadOutlined as DownloadOutlinedIcon } from '@mui/icons-material';

const WorkpaperExport = ({ 
  testingInfo, 
  workpaperDetails, 
  designFiles, 
  interimFiles, 
  rfFiles, 
  finalFiles 
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString();
  };

  // Add HTML tag removal function
  const removeHtmlTags = (str) => {
    if (!str || typeof str !== 'string') return '';
    
    // First decode HTML entities
    const decoded = str.replace(/&nbsp;/g, ' ')
                     .replace(/&amp;/g, '&')
                     .replace(/&lt;/g, '<')
                     .replace(/&gt;/g, '>')
                     .replace(/&quot;/g, '"')
                     .replace(/&#39;/g, "'");
    
    // Then remove HTML tags and normalize whitespace
    return decoded.replace(/<[^>]*>/g, '')
                 .replace(/\s+/g, ' ')
                 .trim();
  };

  // Update the data preparation to include HTML tag removal
  const prepareData = (data) => {
    return data.map(row => 
      row.map(cell => {
        if (typeof cell === 'string') {
          return removeHtmlTags(cell);
        }
        return cell;
      })
    );
  };

  const applyStyles = (ws) => {
    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "2196F3" } },
      alignment: { horizontal: "center" }
    };
    
    const subHeaderStyle = {
      font: { bold: true },
      fill: { fgColor: { rgb: "E3F2FD" } }
    };

    // Get the range of cells in the worksheet
    const range = XLSX.utils.decode_range(ws['!ref']);
    
    // Apply styles to each cell
    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellRef = XLSX.utils.encode_cell({r: R, c: C});
        if (!ws[cellRef]) continue;
        
        // Initialize cell style if it doesn't exist
        if (!ws[cellRef].s) ws[cellRef].s = {};
        
        // Style for first row of each section
        if (ws[cellRef].v && typeof ws[cellRef].v === 'string' && 
            ws[cellRef].v.includes('Information') || 
            ws[cellRef].v.includes('Testing') || 
            ws[cellRef].v.includes('Team') ||
            ws[cellRef].v.includes('Files') ||
            ws[cellRef].v.includes('Details')) {
          Object.assign(ws[cellRef].s, headerStyle);
        }
        // Style for sub-headers
        else if (R === 0 || (ws[cellRef].v && typeof ws[cellRef].v === 'string' && 
                 ['Phase', 'File Name', 'Type', 'Status'].includes(ws[cellRef].v))) {
          Object.assign(ws[cellRef].s, subHeaderStyle);
        }
        
        // Basic cell styling
        ws[cellRef].s = {
          ...ws[cellRef].s,
          border: {
            top: { style: 'thin', color: { rgb: "000000" } },
            bottom: { style: 'thin', color: { rgb: "000000" } },
            left: { style: 'thin', color: { rgb: "000000" } },
            right: { style: 'thin', color: { rgb: "000000" } }
          },
          alignment: { 
            vertical: 'center', 
            horizontal: ws[cellRef].s?.alignment?.horizontal || 'left',
            wrapText: true
          }
        };
      }
    }

    // Set column widths
    const cols = [];
    for (let C = range.s.c; C <= range.e.c; ++C) {
      cols.push({ wch: 20 }); // Set each column to width 20
    }
    ws['!cols'] = cols;

    return ws;
  };

  // Replace the formatName function with this updated version
  const formatName = (email) => {
    if (!email) return '';
    
    // Get the part before the @ symbol
    const namePart = email.split('@')[0];
    
    // Split by dots and get first and last parts
    const parts = namePart.split('.');
    
    if (parts.length > 1) {
      // Get first letter of first part and first letter of last part
      const firstInitial = parts[0].charAt(0);
      const lastInitial = parts[parts.length - 1].charAt(0);
      return `${firstInitial}${lastInitial}`.toUpperCase();
    }
    
    // If no dots in email, just return first two letters
    return namePart.slice(0, 2).toUpperCase();
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      
      // Create workbook
      const wb = XLSX.utils.book_new();

      // Overview sheet data
      const overviewData = prepareData([
        ['Control Information'],
        ['Control ID', testingInfo?.CONTROL_DETAILS?.CONTROL_ID || ''],
        ['Control Title', testingInfo?.CONTROL_DETAILS?.CONTROL_TITLE || ''],
        ['Control Description', testingInfo?.CONTROL_DETAILS?.CONTROL_DESCRIPTION || ''],
        ['Control Type', testingInfo?.CONTROL_DETAILS?.CONTROL_TYPE || ''],
        ['Control Domain', testingInfo?.CONTROL_DETAILS?.CONTROL_DOMAIN || ''],
        ['Control Category', testingInfo?.CONTROL_DETAILS?.CONTROL_CATEGORY || ''],
        ['Status', testingInfo?.STATUS || ''],
        [],
        ['Testing Team'],
        ['Phase', 'Tester', 'Reviewer', 'Tester Sign-off', 'Reviewer Sign-off'],
        ['Design', 
          formatName(workpaperDetails?.[0]?.DESIGN_TESTER),
          formatName(workpaperDetails?.[0]?.DESIGN_REVIEWER),
          formatDate(workpaperDetails?.[0]?.DESIGN_TESTER_SIGNOFF),
          formatDate(workpaperDetails?.[0]?.DESIGN_REVIEWER_SIGNOFF)
        ],
        ['Interim',
          formatName(workpaperDetails?.[0]?.INTERIM_TESTER),
          formatName(workpaperDetails?.[0]?.INTERIM_REVIEWER),
          formatDate(workpaperDetails?.[0]?.INTERIM_TESTER_SIGNOFF),
          formatDate(workpaperDetails?.[0]?.INTERIM_REVIEWER_SIGNOFF)
        ],
        ['Rollforward',
          formatName(workpaperDetails?.[0]?.RF_TESTER),
          formatName(workpaperDetails?.[0]?.RF_REVIEWER),
          formatDate(workpaperDetails?.[0]?.RF_TESTER_SIGNOFF),
          formatDate(workpaperDetails?.[0]?.RF_REVIEWER_SIGNOFF)
        ],
        ['Final',
          formatName(workpaperDetails?.[0]?.FINAL_TESTER),
          formatName(workpaperDetails?.[0]?.FINAL_REVIEWER),
          formatDate(workpaperDetails?.[0]?.FINAL_TESTER_SIGNOFF),
          formatDate(workpaperDetails?.[0]?.FINAL_REVIEWER_SIGNOFF)
        ]
      ]);

      // Design sheet data
      const designData = prepareData([
        ['Design and Implementation Testing'],
        ['Test Type', workpaperDetails?.[0]?.DESIGN_PROCEDURES_TYPE || ''],
        ['Procedures', workpaperDetails?.[0]?.DESIGN_PROCEDURES || ''],
        ['Result', workpaperDetails?.[0]?.DESIGN_RESULT || ''],
        ['Conclusion', workpaperDetails?.[0]?.DESIGN_CONCLUSION || ''],
        [],
        ['Evidence Files'],
        ['File Name', 'Upload Date', 'Size'],
        ...(designFiles || []).map(file => [
          file.original_filename,
          formatDate(file.upload_date),
          `${(file.file_size / 1024).toFixed(2)} KB`
        ])
      ]);

      // Interim sheet data
      const interimData = prepareData([
        ['Interim Testing'],
        ['Applicable', workpaperDetails?.[0]?.INTERIM_APPLICABLE || 'Yes'],
        ['Test Type', workpaperDetails?.[0]?.INTERIM_PROCEDURES_TYPE || ''],
        ['Population Size', workpaperDetails?.[0]?.INTERIM_POPULATION || ''],
        ['Sample Size', workpaperDetails?.[0]?.INTERIM_SAMPLES || ''],
        ['Procedures', workpaperDetails?.[0]?.INTERIM_PROCEDURES || ''],
        ['Result', workpaperDetails?.[0]?.INTERIM_RESULT || ''],
        ['Conclusion', workpaperDetails?.[0]?.INTERIM_CONCLUSION || ''],
        [],
        ['Evidence Files'],
        ['File Name', 'Upload Date', 'Size'],
        ...(interimFiles || []).map(file => [
          file.original_filename,
          formatDate(file.upload_date),
          `${(file.file_size / 1024).toFixed(2)} KB`
        ])
      ]);

      // Rollforward sheet data
      const rfData = prepareData([
        ['Rollforward Testing'],
        ['Applicable', workpaperDetails?.[0]?.RF_APPLICABLE || 'Yes'],
        ['Test Type', workpaperDetails?.[0]?.RF_PROCEDURES_TYPE || ''],
        ['Population Size', workpaperDetails?.[0]?.RF_POPULATION || ''],
        ['Sample Size', workpaperDetails?.[0]?.RF_SAMPLES || ''],
        ['Procedures', workpaperDetails?.[0]?.RF_PROCEDURES || ''],
        ['Result', workpaperDetails?.[0]?.RF_RESULT || ''],
        ['Conclusion', workpaperDetails?.[0]?.RF_CONCLUSION || ''],
        [],
        ['Evidence Files'],
        ['File Name', 'Upload Date', 'Size'],
        ...(rfFiles || []).map(file => [
          file.original_filename,
          formatDate(file.upload_date),
          `${(file.file_size / 1024).toFixed(2)} KB`
        ])
      ]);

      // Final sheet data
      const finalData = prepareData([
        ['Final Testing'],
        ['Applicable', workpaperDetails?.[0]?.FINAL_APPLICABLE || 'Yes'],
        ['Test Type', workpaperDetails?.[0]?.FINAL_PROCEDURES_TYPE || ''],
        ['Population Size', workpaperDetails?.[0]?.FINAL_POPULATION || ''],
        ['Sample Size', workpaperDetails?.[0]?.FINAL_SAMPLES || ''],
        ['Procedures', workpaperDetails?.[0]?.FINAL_PROCEDURES || ''],
        ['Result', workpaperDetails?.[0]?.FINAL_RESULT || ''],
        ['Conclusion', workpaperDetails?.[0]?.FINAL_CONCLUSION || ''],
        [],
        ['Evidence Files'],
        ['File Name', 'Upload Date', 'Size'],
        ...(finalFiles || []).map(file => [
          file.original_filename,
          formatDate(file.upload_date),
          `${(file.file_size / 1024).toFixed(2)} KB`
        ])
      ]);

      // Deficiency sheet data (only if there are deficiencies)
      const hasDeficiency = workpaperDetails?.[0]?.DEFICIENCY_TYPE || 
                           workpaperDetails?.[0]?.DEFICIENCY_DESCRIPTION;
      
      if (hasDeficiency) {
        const deficiencyData = prepareData([
          ['Deficiency Details'],
          ['Type', workpaperDetails?.[0]?.DEFICIENCY_TYPE || ''],
          ['Description', workpaperDetails?.[0]?.DEFICIENCY_DESCRIPTION || ''],
          ['Date Identified', formatDate(workpaperDetails?.[0]?.DATE_IDENTIFIED)],
          ['Root Cause', workpaperDetails?.[0]?.ROOT_CAUSE || ''],
          ['Management Action Plan', workpaperDetails?.[0]?.MANAGEMENT_ACTION_PLAN || ''],
          ['Remediation Owner', workpaperDetails?.[0]?.REMEDIATION_OWNER || ''],
          ['Remediation Status', workpaperDetails?.[0]?.REMEDIATION_STATUS || ''],
          ['Date Remediated', formatDate(workpaperDetails?.[0]?.DATE_REMEDIATED)],
          ['Mitigating Controls', workpaperDetails?.[0]?.MITIGATING_CONTROLS || '']
        ]);
        const ws = XLSX.utils.aoa_to_sheet(deficiencyData);
        applyStyles(ws);
        XLSX.utils.book_append_sheet(wb, ws, 'Deficiency');
      }

      // Add all sheets to workbook
      const overviewWs = XLSX.utils.aoa_to_sheet(overviewData);
      applyStyles(overviewWs);
      XLSX.utils.book_append_sheet(wb, overviewWs, 'Overview');

      const designWs = XLSX.utils.aoa_to_sheet(designData);
      applyStyles(designWs);
      XLSX.utils.book_append_sheet(wb, designWs, 'Design');

      const interimWs = XLSX.utils.aoa_to_sheet(interimData);
      applyStyles(interimWs);
      XLSX.utils.book_append_sheet(wb, interimWs, 'Interim');

      const rfWs = XLSX.utils.aoa_to_sheet(rfData);
      applyStyles(rfWs);
      XLSX.utils.book_append_sheet(wb, rfWs, 'Rollforward');

      const finalWs = XLSX.utils.aoa_to_sheet(finalData);
      applyStyles(finalWs);
      XLSX.utils.book_append_sheet(wb, finalWs, 'Final');

      // Generate Excel file
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const fileName = `${testingInfo?.CONTROL_DETAILS?.CONTROL_ID || 'Workpaper'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      // Save file
      saveAs(new Blob([wbout], { type: 'application/octet-stream' }), fileName);
      
      setShowSuccess(true);
    } catch (error) {
      console.error('Error exporting workpaper:', error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <Chip
        icon={<DownloadOutlinedIcon sx={{ fontSize: "18px" }} />}
        label={isExporting ? "Exporting..." : "Download"}
        onClick={handleExport}
        disabled={isExporting}
        color="primary"
        variant="outlined"
        sx={{
          marginLeft: "auto",
          padding: "4px 8px",
          borderRadius: "16px",
          opacity: isExporting ? 0.7 : 1,
          cursor: isExporting ? 'not-allowed' : 'pointer'
        }}
      />

      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setShowSuccess(false)} 
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          Workpaper exported successfully
        </Alert>
      </Snackbar>
    </>
  );
};

export default WorkpaperExport; 